import React from 'react'

import { Button, Tooltip, useTheme } from '@material-ui/core'
import { useAppDispatch } from '../../hooks'
import {
    BaseWorkorder,
    Company,
    getTransitions,
    User,
    WorkorderStatus,
    WorkSpaceUser,
} from '../../models'
import { transitionWorkorderStatus, WorkorderResponse } from '../../store'
import { Container } from '../Container'
import { toast } from 'react-toastify'
import { VENDOR } from '../../helpers'

type TransitionMethod = (
    event: React.MouseEvent<HTMLButtonElement>,
    workorder: BaseWorkorder,
    status: WorkorderStatus,
) => void

interface Props {
    workorder: BaseWorkorder
    user?: User
    disabled?: boolean
    handleTransition?: TransitionMethod
    style?: React.CSSProperties
    activeWorkspace?: Company
    canRequestEarlyAccess?: boolean
    onCreateWorkorderEarlyAccessRequest?: (workorder: WorkorderResponse) => void
    onClickGoBack?: () => void
}

export const WorkorderTransitionButtons = (props: Props) => {
    const {
        user,
        workorder,
        disabled,
        style,
        canRequestEarlyAccess,
        onCreateWorkorderEarlyAccessRequest,
        onClickGoBack,
    } = props

    const transitions = getTransitions(workorder, user)
    const dispatch = useAppDispatch()
    const theme = useTheme()
    const approvalRoleIds = workorder.approval_role_ids ?? []
    const userPermissionRoleIds =
        user?.permission_roles.map((role) => role.id) ?? []
    const disabledByApprovalRoles =
        workorder.status === WorkorderStatus.COMPLETE &&
        approvalRoleIds.length > 0 &&
        !approvalRoleIds.some((roleId) =>
            userPermissionRoleIds.includes(roleId),
        )
    const transitionDisabled = disabled || disabledByApprovalRoles
    const approvalRoleTooltip = disabledByApprovalRoles
        ? "You don't have the correct permission role for this action."
        : ''

    let clickHandler: TransitionMethod
    if (props.handleTransition) {
        clickHandler = props.handleTransition
    } else {
        clickHandler = (_, workorder, status) => {
            dispatch(
                transitionWorkorderStatus({
                    body: { status: status },
                    workorderId: workorder.id,
                }),
            ).then(() => {
                toast.success(
                    `Workorder transitioned from ${workorder.status} to ${
                        transitions.positive!.to_status
                    }`,
                )
            })
        }
    }

    let displayEarlyAccessButton =
        onCreateWorkorderEarlyAccessRequest !== undefined &&
        props.activeWorkspace &&
        props.activeWorkspace.company_type === VENDOR &&
        workorder.status === WorkorderStatus.ASSIGNED &&
        workorder.approved_early_access_request_id === null

    // Compare today's date with the workorder start date
    if (displayEarlyAccessButton) {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const startDate = new Date(workorder.start_date ?? '')
        startDate.setHours(0, 0, 0, 0)
        displayEarlyAccessButton = today < startDate
    }

    const pendingCount = workorder.pending_request_count ?? 0

    return (
        <Container
            style={{
                justifyContent: 'flex-end',
                margin: theme.spacing(1),
                flexDirection: 'row',
                ...style,
            }}
        >
            {displayEarlyAccessButton && (
                <Button
                    color="primary"
                    variant="outlined"
                    disabled={pendingCount > 0 || !canRequestEarlyAccess}
                    onClick={(e) => {
                        onCreateWorkorderEarlyAccessRequest?.(
                            workorder as WorkorderResponse,
                        )
                    }}
                >
                    Request Early Access
                </Button>
            )}
            {!displayEarlyAccessButton && transitions.positive !== undefined && (
                <Tooltip title={approvalRoleTooltip}>
                    <span>
                        <Button
                            color="primary"
                            variant="outlined"
                            disabled={
                                !transitions.positive.permission ||
                                transitionDisabled
                            }
                            onClick={(e) => {
                                clickHandler(
                                    e,
                                    workorder,
                                    transitions.positive!.to_status,
                                )
                            }}
                        >
                            {transitions.positive.displayString}
                        </Button>
                    </span>
                </Tooltip>
            )}
            {transitions.negative !== undefined && (
                <Tooltip title={approvalRoleTooltip}>
                    <span>
                        <Button
                            color="secondary"
                            variant="outlined"
                            style={{ marginLeft: theme.spacing(1) }}
                            disabled={
                                !transitions.negative.permission ||
                                transitionDisabled
                            }
                            onClick={(e) => {
                                if (onClickGoBack) {
                                    onClickGoBack()
                                } else {
                                    clickHandler(
                                        e,
                                        workorder,
                                        transitions.negative!.to_status,
                                    )
                                }
                            }}
                        >
                            {transitions.negative.displayString}
                        </Button>
                    </span>
                </Tooltip>
            )}
            {transitions.neutral !== undefined && (
                <Tooltip title={approvalRoleTooltip}>
                    <span>
                        <Button
                            color="secondary"
                            variant="outlined"
                            disabled={
                                !transitions.neutral.permission ||
                                transitionDisabled
                            }
                            onClick={(e) => {
                                clickHandler(
                                    e,
                                    workorder,
                                    transitions.neutral!.to_status,
                                )
                            }}
                        >
                            {transitions.neutral.displayString}
                        </Button>
                    </span>
                </Tooltip>
            )}
        </Container>
    )
}
